'use client';

import { JSX, useState } from 'react';
import React from 'react';
import GridContainer from '@/Layouts/GridContainer';
import DefaultPageLayout from '@/Layouts/DefaultPageLayout';
import { fetchMagnoliaDataFor404 } from '@/Util/magnoliaData/fetchMagnoliaDataFor404';
import { getGlobalPageProps } from '@/Util/contentGetters';
import { Link } from '@/Hooks/useReactTransitionProgress';
import ContextProvider from '@/components/contextProviders/ContextProviders';
import { EditablePage } from '@magnolia/react-editor';
import { useSearchParams } from 'next/navigation';
import { useMount } from 'react-use';
import { Props } from '@/types/cms/magnolia';
import SuspenseFallbackComponent from '@/components/SuspenseFallbackComponent/SuspenseFallbackComponent';
import { componentMappingsNotFoundConfig } from '@/Util/componentMappings/magnoliaNotFound';
import { Apis, LogLevel } from '@/Util/globals';

const NotFoundInner = ({
  isLocal,
  isAuthorString,
  isProduction,
  magnoliaApiBase,
  magnoliaHost,
  magnoliaHostClient,
  logLevel,
  apis
}: {
  isLocal?: string;
  isAuthorString?: string;
  isProduction: boolean;
  magnoliaApiBase?: string;
  magnoliaHost?: string;
  magnoliaHostClient?: string;
  logLevel: LogLevel;
  apis: Apis;
}): JSX.Element => {
  const searchParams = useSearchParams();
  const [props, setProps] = useState<Props | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useMount(async () => {
    setProps(
      await fetchMagnoliaDataFor404(
        apis,
        searchParams,
        isProduction,
        logLevel,
        isAuthorString,
        magnoliaApiBase,
        magnoliaHost,
        magnoliaHostClient
      )
    );
    setLoading(false);
  });

  if (loading) {
    return <SuspenseFallbackComponent fixedPosition />;
  }

  if (!loading && !props) {
    return (
      <GridContainer>
        <h3 className="mb-xs-3">There was an error loading the page or the page does not exist.</h3>
        <div>
          <Link href="/">Back to Home</Link>
        </div>
      </GridContainer>
    );
  }

  return !!props ? (
    <DefaultPageLayout pageProps={props}>
      <ContextProvider
        pageProps={JSON.parse(JSON.stringify({ ...getGlobalPageProps(props) }))}
        isProduction={isProduction}
        locale={props.locale}
        albConfig={JSON.parse(JSON.stringify(props.albConfig))}
        isLocal={isLocal || ''}
      >
        {props.page && (
          <EditablePage
            templateAnnotations={props.templateAnnotations}
            content={props.page}
            config={{
              componentMappings: componentMappingsNotFoundConfig.componentMappings,
              fallbackComponent: false
            }}
          />
        )}
      </ContextProvider>
    </DefaultPageLayout>
  ) : (
    <></>
  );
};

export default NotFoundInner;
